// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { AppConstants, LMLocaleUtilService, LMTimeStampDirective } from '@app/utils';
import { LMFieldBaseComponent, LMInvalidFieldEvent } from '@field/lm-field-base.component';
import { LMModel, LMModelChangedEvent } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { SohoDatePickerComponent, SohoInputValidateDirective } from 'ids-enterprise-ng';
import { BehaviorSubject, EMPTY, iif, of, Subject, switchMap, tap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

enum LMDateSubTypeDisplayEnum {
  DATE = 0,
  TEXT,
  TAG
}

@Component({
  selector: 'lm-date',
  templateUrl: './lm-date.component.html',
  styleUrls: ['lm-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
  })
export class LMDateComponent extends LMFieldBaseComponent implements OnInit, OnDestroy {
  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @HostBinding('attr.data-automation-id') dataFieldAutomationId = '';

  @ViewChild(SohoInputValidateDirective) validate: SohoInputValidateDirective;

  @ViewChild(SohoDatePickerComponent) date: SohoDatePickerComponent;

  public showTime: boolean = undefined;
  public timeFormat: string = undefined;
  public mode: SohoDatePickerMode;
  public enteredValue: string;
  public updateDisplay$: BehaviorSubject<string> = new BehaviorSubject<string>('');


  // see DateYMD.java
  // these dates are one day less and one day more than the min and max so
  // that the datepicker UI correctly displays the correct range.
  public disableOptions: SohoDatePickerDisable = {
    dates:     '',
    minDate:   this.lmFieldServiceAdapter.lmFormatUtilService.minDate,
    maxDate:   this.lmFieldServiceAdapter.lmFormatUtilService.maxDate,
    dayOfWeek: []
  };

  public pickerOptions: SohoDatePickerOptions = {
    disable: this.disableOptions,
    incrementWithKeyboard: true,
    todayWithKeyboard: true
  };

  public displayType: LMDateSubTypeDisplayEnum;
  public layoutDisplayType;

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private lmLocaleUtilService: LMLocaleUtilService) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMDateSubTypeDisplayEnum;
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  onError(event: SohoInputValidateEvent) {
    super.onError(event);

    // only block save on non-server side errors (invalid date and date out of range)
    if (!this.serverSideError) {
      if (this.enteredValue && this.sohoAlert) {
        const invalidEvent = new LMInvalidFieldEvent(
            new CustomEvent('ValueChanged'), 'BlockingTransactionFieldEvent', this.bindId, this.sohoAlert);
        this.fieldEvent.emit(invalidEvent);
      }
    }
  }

  onValid(event: SohoInputValidateEvent) {
    super.onValid(event);
    this.model.setBlockTransactionError(this.bindId, false, null);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);
    this.setOptions();
    this.setSize();
    this.setFieldClasses();
    this.subscribeToTimezoneField();

    this.dataFieldAutomationId = `lm-${this.fieldAutomationId}-field`;

    if (this.displayAsTag && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TAG;

    } else if (this.displayAsText && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TEXT;

    } else {
      this.subscribeToInlineError();
      this.displayType = this.layoutDisplayType.DATE;
    }

    // This a workaround because the soho-datepicker control does not hold it's
    // isReadOnly value correctly on initialization. Can use the template with the
    // readOnly$ async pipe once this is fixed in the next controls update
    this.subscriptionArray.push(this.readOnly$.subscribe((disable: boolean) => {
      // need to wrap in a setTimeout so the control's state has finished updating
      // before setting anything
      setTimeout(() => {
        if (this.date) {
          this.date.readonly = disable;
        }
      });
    }));

    this.subscriptionArray.push(this.modelField.valueChanged.subscribe((value) => {
      if (value != undefined && !this.lmFieldServiceAdapter.lmFieldUtilService.isBlank(this.specField.type, value)) {
        // actual value entered, update the display value
        this.transformOptions.doNotTransform = false;
        this.updateDisplay$.next(value);
      } else {
        // blank value
        const isBlank = this.lmFieldServiceAdapter.lmFieldUtilService.isBlank(this.specField.type, value);
        if ((this.enteredValue != undefined && this.enteredValue !== '') && isBlank) {
          // invalid entered value, just re-display
          this.transformOptions.doNotTransform = true;
          this.model.setBlockTransactionError(this.bindId, true, new LMInvalidFieldEvent(undefined, 'FieldInvalidEvent', this.bindId, this.sohoAlert));
          this.updateDisplay$.next(this.enteredValue);
        } else {
          // no set entered value, so change is coming from a dataview change. Update the display value
          this.updateDisplay$.next(value);
        }
      }
      // validate will run 300ms after it gets an event. This means that if a user types in the field and then
      // immediately clicks an action button, the error modal will be displayed before IDS runs validation. If
      // IDS sees that a modal is open, it will not run validation.
      // So we'll run validation here, to make sure the field is marked appropriately.
      if (this.validate) {
        this.validate.validate(new Event('changed'));
      }
    }));

    this.subscriptionArray.push(this.model.modelChanged.subscribe((event: LMModelChangedEvent) => {
      if (event.eventType === 'update' || event.eventType === 'set') {
        this.enteredValue = undefined;
        this.updateDisplay$.next(this.modelField.value);
      }
    }));

    this.subscriptionArray.push(this.modelField.clearState.subscribe((value) => {
      if (value) {
        // value is being blanked from the reset blocking date errors popup from lm-form. Make sure the
        // display value is cleared.
        this.updateDisplay$.next(this.modelField.value);
      }
    }));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public onNgModelChange(value) {
    this.enteredValue = value;
    if (!this.model.isSearchForm) {
      if (!this.enteredValueWithinRange(this.enteredValue)) {
        // value is out of range, set the value to blank
        value = '';
      }
    }
    if (this.transformOptions.timeStampDirective && this.transformOptions.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
      this.transformOptions.timeStampDataValue = this.modelField.value;
    }
    this.modelField.toDataValue(value, this.transformOptions);
  }

  // private methods
  private setOptions() {
    if (this.modelField.filterCriteria?.operator === 'inRange') {
      this.pickerOptions.mode = 'range';
      this.mode = 'range';
    } else {
      this.pickerOptions.mode = 'standard';
      this.mode = 'standard';
    }

    this.setDateFormat();
  }

  private subscribeToTimezoneField() {
    if (this.specField.type === AppConstants.TIMESTAMP && this.layoutObject.timeZoneIs) {
      this.transformOptions.timeZone = this.model.getField(this.layoutObject.timeZoneIs).value;
      this.subscriptionArray.push(this.model.timeZoneChangedEvent$
        // this pipe acts as an internal cache to check if a timestamp field with the same id is already processed
        // this is to avoid re-evaluating each field with the same id that subscribes to time zone field 
        .pipe(
          switchMap(data => {
            // update transform option time zone value for pipe transform
            this.transformOptions.timeZone = data.timeZone;
            // if the timestamp bind id is already processed, skip (EMPTY)
            return iif(() => data.subscribingBindIds?.includes(this.specField.bindId), EMPTY, of(data));
          }),
          tap(data => {
            data.subscribingBindIds = data.subscribingBindIds || [];
            if (!data.subscribingBindIds?.includes(this.specField.bindId)) {
              data.subscribingBindIds.push(this.specField.bindId);
            }
          })
        )
        .subscribe(({ bindId, timeZone, previousTimeZone }) => {
          if (bindId === this.layoutObject.timeZoneIs) {
            this.transformOptions.timeZone = previousTimeZone;
            const displayValue = this.lmFieldServiceAdapter.lmFormatUtilService.toDisplayValue(
              this.modelField.value, this.specField.type, { timeZone: this.transformOptions.timeZone, disableDST: this.transformOptions.disableDST });
            this.transformOptions.timeZone = timeZone;
            this.modelField.value = this.lmFieldServiceAdapter.lmFormatUtilService.toDataValue(
              displayValue, this.specField.type, { timeZone: this.transformOptions.timeZone, disableDST: this.transformOptions.disableDST });
          }
        }));
    }
  }

  onDateChange() {
    if (!this.hasFieldChanged) {
      // came back from the picker - user did not type in the input
      // lm-field-base has a change host listener that is
      // handling user input (typing in a value)
      this.onChange();
      this.handleOnFieldChanged();
    }
  }

  onFiltered(event: SohoFieldFilteredEvent) {
    if (event.filterOption.value === 'in-range') {
      this.mode = 'range';
      // Turning off the keyboard shortcuts t and +/- when in range, since they will overwrite the entered
      // dates. They are really only for single date entry purposes.
      this.pickerOptions.incrementWithKeyboard = false;
      this.pickerOptions.todayWithKeyboard = false;

      if (this.specField.type === AppConstants.TIMESTAMP) {
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
          + Soho.Locale.calendar().dateFormat.timestamp;
        this.pickerOptions.showTime = true;
      } else {
        this.setDateFormat();
      }
    } else {
      this.mode = 'standard';
      // Turning the keyboard chortcuts t and +/- back on for non in range date filtering
      this.pickerOptions.incrementWithKeyboard = true;
      this.pickerOptions.todayWithKeyboard = true;

      switch (this.specField.type) {
        // date and time
        case AppConstants.TIMESTAMP:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
            + Soho.Locale.calendar().dateFormat.timestamp;
          this.pickerOptions.showTime = true;
          break;

        // year only
        case AppConstants.YYYY:
          this.pickerOptions.dateFormat =  'yyyy';
          this.pickerOptions.showMonthYearPicker = true;
          this.pickerOptions.hideDays = true;
          break;

        // period / birthday
        case AppConstants.YYYYMM:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.year;
          this.pickerOptions.showMonthYearPicker = true;
          this.pickerOptions.hideDays = true;
          break;

        // year, month, day
        case AppConstants.YYYYMMDD:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short;
          this.pickerOptions.showMonthYearPicker = true;
          break;

        // Anniversary
        case AppConstants.MMDD:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.month;
          break;
      }
    }
    super.onFiltered(event);
  }

  onEnterUp(){
    this.onNgModelChange((this.date as any).jQueryElement.val());
  }

  // Determine if the entered value is within the min and max dates defined in LMFormatUtilService and DateYMD.java
  private enteredValueWithinRange(value): boolean {
    let fromPattern = '';
    let toPattern = '';
    if (this.specField.type === AppConstants.YYYYMMDD) {
      fromPattern = Soho.Locale.calendar().dateFormat.short;
      toPattern = this.lmLocaleUtilService.calendar.YYYYMMDD.pattern;
    } else if (this.specField.type === AppConstants.YYYYMM) {
      fromPattern = Soho.Locale.calendar().dateFormat.year;
      toPattern = this.lmLocaleUtilService.calendar.YYYYMM.pattern;
    } else if (this.specField.type === AppConstants.MMDD) {
      fromPattern = Soho.Locale.calendar().dateFormat.month;
      toPattern = this.lmLocaleUtilService.calendar.MMDD.pattern;
    } else if (this.specField.type === AppConstants.YYYY) {
      fromPattern = 'yyyy';
      toPattern = this.lmLocaleUtilService.calendar.YYYY.pattern;
    } else if (this.specField.type === AppConstants.TIMESTAMP) {
      // Need to handle the different directives for timestamp
      if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
        fromPattern = Soho.Locale.calendar().dateFormat.short;
        toPattern = this.lmLocaleUtilService.calendar.YYYYMMDD.pattern;
      } else if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_HOURS_AND_MINUTES) {
        fromPattern = Soho.Locale.calendar().timeFormat;
        toPattern = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
      } else if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_TIME) {
        fromPattern = Soho.Locale.calendar().dateFormat.timestamp;
        toPattern = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
      } else if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES) {
        fromPattern = Soho.Locale.calendar().dateFormat.datetime;
        toPattern = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
      } else {
        fromPattern = Soho.Locale.calendar().dateFormat.short + ' ' + Soho.Locale.calendar().dateFormat.timestamp;
        toPattern = this.lmLocaleUtilService.calendar.TIMESTAMP.pattern;
      }
    }

    const landmarkParseDateOptions: SohoLocaleParseDateOptions = {
      dateFormat: fromPattern,
      locale: this.lmLocaleUtilService.name,
      calendarName: this.lmLocaleUtilService.calendarName
    };

    let enteredDate = Soho.Locale.parseDate(value, landmarkParseDateOptions, false);

    if (enteredDate === undefined) {
      return false;
    }

    const hijriDate = this.field.hasOwnProperty('displayHijriDate');
    if (hijriDate) {
      // Hijri needs to be converted to gregorian to determine if it's in the correct range. This is because
      // the hijri calendar is currently in year 1445, which is out of the date range. Timestamp and hijri
      // is not supported currently
      if (this.specField.type !== AppConstants.TIMESTAMP) {
        const formatDateAttribs: any = {
          pattern: toPattern,
          locale: this.lmLocaleUtilService.name,
          calendarName: this.lmLocaleUtilService.calendarName,
          toGregorian: true
        };

        landmarkParseDateOptions.dateFormat = toPattern;

        const hijriDateString = Soho.Locale.formatDate(enteredDate, formatDateAttribs);
        enteredDate = Soho.Locale.parseDate(hijriDateString, landmarkParseDateOptions, false);
      }
    }

    return this.lmFieldServiceAdapter.lmFormatUtilService.minDate < enteredDate && enteredDate < this.lmFieldServiceAdapter.lmFormatUtilService.maxDate;
  }

  private setDateFormat(): void {
    let type = this.specField.type;

    if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
      type = AppConstants.YYYYMMDD;
    }

    // TODO: need to support TIMESTAMP for hijri and gregorian
    if (type === AppConstants.YYYYMMDD && this.timeStampDirective === undefined) {
      if (this.field.hasOwnProperty('displayHijriDate')) {
        // TODO: //maf I was thinking after all the preliminary work in ep, etc that we would not need to pass a locale
        // here, but passing one is the only way to get the correct umalqura calendar. There may be some work yet in ep.
        // When the locale is not passed, I get the a calendar, but it is not the same as when the locale is used,
        // meaning the calendar has en-US month names and en-US days, but the year is like 1441.
        this.pickerOptions.locale = 'ar-SA';
        this.pickerOptions.calendarName = 'islamic-umalqura';

        this.transformOptions.locale = 'ar-SA';
        this.transformOptions.calendarName = 'islamic-umalqura';

      } else if (this.field.hasOwnProperty('displayGregorianDate')) {
        this.pickerOptions.calendarName = 'gregorian';
        this.transformOptions.calendarName = 'gregorian';
      }
    }

    switch (type) {
        // anniversary
      case AppConstants.MMDD:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.month;
        break;

        // date and time
      case AppConstants.TIMESTAMP:
        this.pickerOptions.showTime = true;
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.useCurrentTime = true;

        if (this.lmFieldServiceAdapter.lmFieldLayoutService.hasLimitToMinuteIncrements(this.field, this.specField)
            || this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayDateAndHoursAndMinutes(this.field, this.specField)) {
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.datetime;
          this.pickerOptions.minuteInterval = this.layoutObject.limitToMinuteIncrements;
        } else {
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
              + Soho.Locale.calendar().dateFormat.timestamp;
          this.pickerOptions.secondInterval = 1;
          this.pickerOptions.minuteInterval = 1;
        }
        break;

        // year only
      case AppConstants.YYYY:
        this.pickerOptions.dateFormat =  'yyyy';
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.hideDays = true;
        break;

        // period / birthday
      case AppConstants.YYYYMM:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.year;
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.hideDays = true;
        break;

        // year, month, day
      case AppConstants.YYYYMMDD:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short;
        this.pickerOptions.showMonthYearPicker = true;
        break;
    }
  }
}
